import axios from "axios"
import { useAppStore } from "../store"
import { resolveBackendBase } from "./backendBase"

/** Тот же origin, что и [`src/api.ts`](../api.ts): один бэкенд для витрины и CRM-админки. */
const BACKEND = resolveBackendBase()

// SaaS: Dynamic tenant detection by subdomain
// Example: my-shop.onviz.app -> tenant lookup
const getTenantId = (): string => {
  // Priority 1: Explicit env variable (for dev/SaaS admin)
  const envTenant = import.meta.env.VITE_TENANT_ID
  if (envTenant) return envTenant

  // Priority 2: Subdomain-based tenant (resolved server-side via X-Tenant-ID middleware)
  // The backend maps subdomains to tenant UUIDs — no client-side map needed.
  return '00000000-0000-0000-0000-000000000000'
}

export const getCurrentTenantId = getTenantId

export const api = axios.create({
  baseURL: `${BACKEND}/api`,
  timeout: 90_000,
  headers: {
    "Content-Type": "application/json",
    "X-Tenant-ID": getTenantId(),
  },
})

api.interceptors.request.use((config) => {
  const token = useAppStore.getState().token
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Response interceptor for errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// API Types
export interface Category {
  id: number
  slug: string
  name: string
  title: string
  description?: string
  image_url?: string
  is_active: boolean
}

export interface System {
  id: number
  slug: string
  title: string
  short_description?: string
  base_image_url?: string
  category_id: number
  is_active: boolean
}

export interface PriceResponse {
  base_price: number
  final_price: number
  currency: string
  breakdown: Array<{
    name: string
    amount: number
  }>
}

// API Functions
export const catalogApi = {
  getCategories: () => api.get<Category[]>("/v3/categories"),
  getSystems: (categorySlug?: string) => 
    api.get<System[]>("/v3/systems", { params: { category_slug: categorySlug } }),
  getSystem: (slug: string) => api.get<System>(`/v3/systems/${slug}`),
  calculatePrice: (slug: string, params: Record<string, any>) =>
    api.post<PriceResponse>(`/v3/constructor/${slug}/calculate`, params),
}

// Admin API Functions
export const adminApi = {
  // Systems CRUD
  createSystem: (data: Omit<System, "id">) => 
    api.post<System>("/admin/systems", data),
  updateSystem: (id: number, data: Partial<System>) => 
    api.put<System>(`/admin/systems/${id}`, data),
  deleteSystem: (id: number) => 
    api.delete(`/admin/systems/${id}`),
  
  // Categories CRUD
  createCategory: (data: Omit<Category, "id">) =>
    api.post<Category>("/admin/categories", data),
  updateCategory: (id: number, data: Partial<Category>) =>
    api.put<Category>(`/admin/categories/${id}`, data),
  deleteCategory: (id: number) =>
    api.delete(`/admin/categories/${id}`),
  
  // Stats for Dashboard
  getStats: () => api.get<{
    totalOrders: number
    todayOrders: number
    totalRevenue: number
    activeUsers: number
    productsCount: number
    conversionRate: number
  }>("/admin/stats"),
  
  // Product Image Upload
  uploadProductImage: (productId: number, file: File) => {
    const formData = new FormData()
    formData.append('uploaded', file)
    return api.post<{ok: boolean, url: string, id: number}>(`/admin/products/${productId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  uploadAccessoryImage: (accessoryId: number, file: File) => {
    const formData = new FormData()
    formData.append('uploaded', file)
    return api.post<{ ok: boolean; url: string; id: number }>(
      `/admin/accessories/${accessoryId}/upload-image`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  },

  uploadAccessoryVitrineGroupImage: (groupId: number, file: File) => {
    const formData = new FormData()
    formData.append('uploaded', file)
    return api.post<{ ok: boolean; url: string; id: number }>(
      `/admin/accessory-vitrine-groups/${groupId}/upload-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  },
  
  // System Media Management
  getSystemMedia: (systemId: number) => 
    api.get<{id: number, url: string, type: string, sort_order: number, is_primary: boolean}[]>(`/admin/catalog-systems/${systemId}/media`),
  uploadSystemMedia: (systemId: number, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/admin/catalog-systems/${systemId}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteSystemMedia: (systemId: number, mediaId: number) =>
    api.delete(`/admin/catalog-systems/${systemId}/media/${mediaId}`),
  setPrimaryMedia: (systemId: number, mediaId: number) =>
    api.patch(`/admin/catalog-systems/${systemId}/media/${mediaId}/primary`, {}),
  reorderSystemMedia: (systemId: number, mediaIds: number[]) =>
    api.post(`/admin/catalog-systems/${systemId}/media/reorder`, { media_ids: mediaIds }),
}
